import { localizedStrings } from '@core/strings'
import React from 'react'
import { Box, Text } from '@fundamentals'
import {
  LinkInspectionsQuestionRules,
  QuestionTypeComponentType,
} from '@core/components/templateAnswersForm'
import { LinkedChecklistResult } from '@core/entities/linkedProcesses'
import { Checklist } from '@core/entities/checklist/Checklist'
import { Grid } from '@mui/material'
import { ChecklistCard } from '../../Cards'
import { Button, IconButton, Tooltip } from '@common/material'
import LaunchIcon from '@mui/icons-material/Launch'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import { SelectOrganisationChecklistsModal } from '@elements'
import { useNavigationContext } from '@common/hooks'
import { useFormContext } from 'react-hook-form'
import { useModal } from '@common/GlobalModal'
import ChecklistDetailsModal from '@modules/permitDetails/modals/ChecklistDetailsModal'

const LinkInspectionsQuestion: QuestionTypeComponentType = ({
  question,
  inputProps: { value = question, onChange } = {},
  readOnly,
}) => {
  const { organisationId } = useNavigationContext()
  const formContext = useFormContext()
  const projectId = formContext?.getValues('projectId')
  const { showModal } = useModal()

  const onChangeSelectedInspections = (
    inspectionIds: Array<number>,
    inspections: Array<Checklist>,
  ) => {
    onChange({
      ...value,
      linkedInspectionsIds: inspectionIds,
      linkedInspectionsResults:
        inspections as unknown as Array<LinkedChecklistResult>,
    })
  }

  const removeSelectedInspection = (inspectionId: number) => {
    onChange({
      ...value,
      linkedInspectionsIds: value.linkedInspectionsIds.filter(
        (id) => id !== inspectionId,
      ),
      linkedInspectionsResults: value.linkedInspectionsResults.filter(
        (inspection) => inspection.id !== inspectionId,
      ),
    })
  }

  const templateIds =
    value?.inspectionTemplateLinks?.map((link) => link.id) ??
    value?.inspectionTemplateLinkingIds

  return (
    <Box data-test={`${question.id}-LINK_INSPECTIONS`}>
      {readOnly && (
        <Text color='text.secondary' fontSize='12px' sx={{ mb: 1 }}>
          {localizedStrings.linkedDocuments}
        </Text>
      )}

      <Grid container spacing={1.5}>
        {question?.linkedInspectionsResults?.map(
          (linkedInspectionResult, index) => {
            const { id, templateName, ...linkedInspectionResultEntity } =
              LinkedChecklistResult.new(linkedInspectionResult)
            return (
              <Grid key={index} item xs={12}>
                <ChecklistCard
                  templateName={templateName}
                  sx={{ height: '100%' }}
                  icon={
                    readOnly ? (
                      <IconButton
                        color='primary'
                        size='small'
                        onClick={() =>
                          showModal(
                            <ChecklistDetailsModal
                              organisationId={organisationId}
                              checklistId={id}
                            />,
                          )
                        }
                      >
                        <Tooltip title={localizedStrings.viewInspection}>
                          <LaunchIcon fontSize='small' />
                        </Tooltip>
                      </IconButton>
                    ) : (
                      <IconButton
                        color='error'
                        onClick={() => removeSelectedInspection(id)}
                      >
                        <RemoveCircleIcon />
                      </IconButton>
                    )
                  }
                  {...linkedInspectionResultEntity}
                />
              </Grid>
            )
          },
        )}
      </Grid>

      {!readOnly && (
        <SelectOrganisationChecklistsModal
          title={localizedStrings.selectInspectionsToLink}
          organisationId={organisationId}
          value={value.linkedInspectionsIds}
          onChange={onChangeSelectedInspections}
          triggerComponent={(onClick) => (
            <Button variant='contained' onClick={onClick} sx={{ mt: 1.5 }}>
              {localizedStrings.addDocuments}
            </Button>
          )}
          filters={{
            templateIds,
            statuses: value?.inspectionTemplateLinkingStatuses,
            projectIds: [projectId],
            isUsedForInspections: true,
          }}
          multiSelect
        />
      )}
    </Box>
  )
}

LinkInspectionsQuestion.rules = LinkInspectionsQuestionRules

export { LinkInspectionsQuestion }
